/**
 * API wrappers for the self-service Data Source Connector endpoints
 * (Stories 9.6, 9.7, 9.8, 9.9). Every function returns the FULL response
 * envelope — callers are expected to unwrap `.data` to get the payload,
 * mirroring the convention already used by `client.ts` and the rest of
 * the reporting frontend.
 *
 * Envelope shape (from internal/api/response.Response):
 *   { data: T, meta?: { total, limit, offset }, error?: { code, message } }
 */

import api from './client';
import type {
  Connection,
  ConnectionCreateRequest,
  ConnectionUpdateRequest,
  ContractListResponse,
  SyncEntity,
  SyncEntityRequest,
  SyncNowResponse,
  SyncRun,
  TestResult,
} from '../types/connector';

/** Response envelope returned by all connector endpoints. */
export interface ApiEnvelope<T> {
  data: T;
  meta?: {
    total?: number;
    limit?: number;
    offset?: number;
  };
  error?: {
    code: string;
    message: string;
  };
}

// -------------------- Connection CRUD (Story 9.6) --------------------

export async function listConnections(params?: {
  site_id?: number;
  limit?: number;
  offset?: number;
}): Promise<ApiEnvelope<Connection[]>> {
  const response = await api.get('/connectors/connections', { params });
  return response.data;
}

export async function getConnection(
  id: string,
  siteId?: number,
): Promise<ApiEnvelope<Connection>> {
  const response = await api.get(`/connectors/connections/${id}`, {
    params: siteId ? { site_id: siteId } : undefined,
  });
  return response.data;
}

export async function createConnection(
  body: ConnectionCreateRequest,
): Promise<ApiEnvelope<Connection>> {
  const response = await api.post('/connectors/connections', body);
  return response.data;
}

export async function updateConnection(
  id: string,
  body: ConnectionUpdateRequest,
  siteId?: number,
): Promise<ApiEnvelope<Connection>> {
  const response = await api.put(`/connectors/connections/${id}`, body, {
    params: siteId ? { site_id: siteId } : undefined,
  });
  return response.data;
}

export async function deleteConnection(id: string, siteId?: number): Promise<void> {
  await api.delete(`/connectors/connections/${id}`, {
    params: siteId ? { site_id: siteId } : undefined,
  });
}

/**
 * Runs the Test Connection handshake. ALWAYS resolves with a TestResult —
 * connectivity failures are surfaced in `reachable` / `overall_status`,
 * not as thrown exceptions. Only genuine API bugs (500, 503, etc.) throw.
 */
export async function testConnection(
  id: string,
  siteId?: number,
): Promise<ApiEnvelope<TestResult>> {
  const response = await api.post(
    `/connectors/connections/${id}/test`,
    null,
    { params: siteId ? { site_id: siteId } : undefined },
  );
  return response.data;
}

// -------------------- Sync control (Story 9.7) --------------------

export async function listSyncRuns(
  connectionId: string,
  params?: { entity?: string; limit?: number; site_id?: number },
): Promise<ApiEnvelope<SyncRun[]>> {
  const response = await api.get(
    `/connectors/connections/${connectionId}/sync-runs`,
    { params },
  );
  return response.data;
}

export async function triggerFullResync(
  connectionId: string,
  entity: string,
  siteId?: number,
): Promise<ApiEnvelope<{ status: string }>> {
  const response = await api.post(
    `/connectors/connections/${connectionId}/entities/${encodeURIComponent(entity)}/full-resync`,
    null,
    { params: siteId ? { site_id: siteId } : undefined },
  );
  return response.data;
}

export async function cancelSync(
  connectionId: string,
  entity: string,
  siteId?: number,
): Promise<ApiEnvelope<{ status: string }>> {
  const response = await api.post(
    `/connectors/connections/${connectionId}/entities/${encodeURIComponent(entity)}/cancel`,
    null,
    { params: siteId ? { site_id: siteId } : undefined },
  );
  return response.data;
}

// -------------------- Entity management + sync-now (Story 9.8) --------------------

export async function listEntities(
  connectionId: string,
  siteId?: number,
): Promise<ApiEnvelope<SyncEntity[]>> {
  const response = await api.get(
    `/connectors/connections/${connectionId}/entities`,
    { params: siteId ? { site_id: siteId } : undefined },
  );
  return response.data;
}

export async function upsertEntity(
  connectionId: string,
  entity: string,
  body: SyncEntityRequest,
  siteId?: number,
): Promise<ApiEnvelope<SyncEntity>> {
  const response = await api.put(
    `/connectors/connections/${connectionId}/entities/${encodeURIComponent(entity)}`,
    body,
    { params: siteId ? { site_id: siteId } : undefined },
  );
  return response.data;
}

export async function triggerSyncNow(
  connectionId: string,
  entity: string,
  siteId?: number,
): Promise<ApiEnvelope<SyncNowResponse>> {
  const response = await api.post(
    `/connectors/connections/${connectionId}/entities/${encodeURIComponent(entity)}/sync-now`,
    null,
    { params: siteId ? { site_id: siteId } : undefined },
  );
  return response.data;
}

// -------------------- Public Data Source Contract (Story 9.4) --------------------

/**
 * Returns the published Data Source Contract. This endpoint is PUBLIC
 * (no JWT) so operators can browse the contract before onboarding.
 *
 * Shape note (fixed 2026-04-10): unlike the RBAC-protected connector routes,
 * the public contract handler returns the raw `{drivers, entities}` payload
 * WITHOUT the `response.Response` envelope. We wrap it here into the shared
 * ApiEnvelope shape so all callers (EntitiesTab, DataSourceContract page) can
 * keep unwrapping via `.data.entities` like every other connector endpoint.
 */
export async function listContractEntities(): Promise<
  ApiEnvelope<ContractListResponse>
> {
  const response = await api.get('/connectors/contract/entities');
  const raw = response.data as ContractListResponse;
  return { data: raw };
}
